import React from 'react'
import { AxiosError } from 'axios'
import { action, computed, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'

import {
	AppDateFormats,
	AppDateWithTimeFormats,
	DEFAULT_APP_DATE_FORMAT,
	DEFAULT_APP_DATE_WITH_TIME_FORMAT,
	DEFAULT_APP_TIME_FORMAT
} from 'Core/constants'
import {
	AxiosErrorResponse,
	defaultInfiniteLoaderData,
	DrawerProps,
	InfiniteLoaderData,
	LoadingBtnNames,
	ModalProps,
	NotificationProps,
	SidePreviewProps
} from 'Core/interfaces'
import notificationService from 'Core/services/NotificationService'
import HistoryStore from 'Core/stores/historyStore/HistoryStore'

import { NO_FILTERS_ERROR_MESSAGE } from 'Portals/Internal/constants'

export default class UIStore<RootStore extends HistoryStore> {
	@observable
	accessor modalStack: ModalProps[] = []
	@observable
	accessor sidePreviewProps: SidePreviewProps | null = null

	@observable
	accessor drawerProps: DrawerProps | null = null

	@observable
	accessor isSidePreviewOpened = false

	@observable
	accessor isDrawerOpened = false

	@observable
	accessor isSidebarOpened = false

	@observable
	accessor isRequestSubmitting = false

	@observable
	accessor btnLoadingName: LoadingBtnNames | null = null

	@observable
	accessor dateFormat: AppDateFormats | null = null

	@observable
	accessor isDateFormatLoading: boolean = false

	@observable
	accessor infiniteLoaderData: InfiniteLoaderData = defaultInfiniteLoaderData

	@observable
	accessor isFilterPanelOpen = true

	constructor(public rootStore: RootStore) {
	}

	@action
	showNotification = (props: NotificationProps) => {
		notificationService.showNotification(props)
	}

	showSuccessNotification = (message: string) => notificationService.showSuccessNotification(message)

	showErrorNotification = (error?: AxiosError, msg?: string) =>
		notificationService.showErrorNotification(error, msg)

	@computed
	get isModalOpened(): boolean {
		return this.modalStack.length > 0
	}

	@computed
	get modalProps(): ModalProps | null {
		return this.modalStack.length > 0 ? this.modalStack[this.modalStack.length - 1] : null
	}

	@action
	openModal = (payload: ModalProps) => {
		this.modalStack.push(payload)
	}

	@action
	updateModalTitle = (title?: string) => {
		if (this.modalStack.length > 0) {
			const currentModal = this.modalStack[this.modalStack.length - 1]
			this.modalStack[this.modalStack.length - 1] = { ...currentModal, title }
		}
	}

	@action
	updateModalWidth = (width: number) => {
		if (this.modalStack.length > 0) {
			const currentModal = this.modalStack[this.modalStack.length - 1]
			this.modalStack[this.modalStack.length - 1] = { ...currentModal, width }
		}
	}

	@action
	updateModalClosable = (closable: boolean) => {
		if (this.modalStack.length > 0) {
			const currentModal = this.modalStack[this.modalStack.length - 1]
			this.modalStack[this.modalStack.length - 1] = { ...currentModal, closable }
		}
	}

	@action
	openSidePreview = (payload: SidePreviewProps) => {
		this.isSidePreviewOpened = true
		this.sidePreviewProps = payload
	}

	@action
	closeModal = () => {
		if (this.modalStack.length > 0) {
			const currentModal = this.modalStack[this.modalStack.length - 1]
			if (currentModal && currentModal.onClose) {
				currentModal.onClose()
			}
			this.modalStack.pop()
		}
	}

	@action
	openDrawer = (payload: DrawerProps) => {
		this.isDrawerOpened = true
		this.drawerProps = payload
	}

	@action.bound
	refreshDrawerProps(payload: DrawerProps) {
		this.drawerProps = payload
	}

	@action
	setIsDrawerOpened = (value: boolean) => {
		this.isDrawerOpened = value
	}

	@action
	closeDrawer = async (event?: React.MouseEvent) => {
		if (event) {
			this.drawerProps?.onClose?.(event)
		}
		this.isDrawerOpened = false
	}

	@action.bound
	resetDrawerProps() {
		if (!this.isDrawerOpened) {
			this.drawerProps = null
		}
	}

	@action
	closeModalWithoutProps = () => {
		if (this.modalStack.length > 0) {
			this.modalStack.pop()
		}
	}

	@action
	closeAllModals = () => {
		// Call onClose for all modals that have it defined
		this.modalStack.forEach(modal => {
			if (modal.onClose) {
				modal.onClose()
			}
		})
		this.modalStack.splice(0, this.modalStack.length)
	}

	@computed
	get modalCount(): number {
		return this.modalStack.length
	}

	@action
	closeSidePreview = () => {
		if (this.sidePreviewProps && this.sidePreviewProps.onClose) {
			this.sidePreviewProps.onClose()
		}

		this.isSidePreviewOpened = false
		this.sidePreviewProps = null
	}

	@action
	openSidebar = () => {
		this.isSidebarOpened = true
	}

	@action
	closeSidebar = () => {
		this.isSidebarOpened = false
	}

	@action
	setIsRequestSubmitting = (status?: boolean) => {
		this.isRequestSubmitting = status ?? true
	}

	@action
	setIsBtnLoading = (btnName: LoadingBtnNames | null, status?: boolean) => {
		this.setIsRequestSubmitting(status)
		this.btnLoadingName = btnName
	}

	getIsBtnLoading = computedFn((btnName: LoadingBtnNames) => this.btnLoadingName === btnName)

	getIsBtnDisabled = computedFn(
		(btnName: LoadingBtnNames) => this.isRequestSubmitting && this.btnLoadingName !== btnName
	)

	getBtnConfig = (btnName: LoadingBtnNames, isDisabled?: boolean, isLoading?: boolean) => ({
		loading: this.getIsBtnLoading(btnName) || isLoading,
		disabled: this.getIsBtnDisabled(btnName) || isDisabled
	})

	@action.bound
	setIsDateFormatLoading(isLoading: boolean) {
		this.isDateFormatLoading = isLoading
	}

	@action
	setCurrentDateFormat = (dateFormat: AppDateFormats) => {
		if (!dateFormat) {
			return
		}

		this.dateFormat = dateFormat.toUpperCase() as AppDateFormats
	}

	@computed
	get getCurrentDateFormat(): AppDateFormats {
		if (!this.dateFormat) {
			return DEFAULT_APP_DATE_FORMAT
		}

		return this.dateFormat
	}

	@computed
	get getCurrentDateWithTimeFormat(): AppDateWithTimeFormats {
		if (!this.dateFormat) {
			return DEFAULT_APP_DATE_WITH_TIME_FORMAT
		}

		return `${this.dateFormat} ${DEFAULT_APP_TIME_FORMAT}`
	}

	@computed
	get getPlaceholderDateFormat(): string {
		return this.getCurrentDateFormat.toLowerCase()
	}

	backToPreviousPage = () => {
		const prevPagePath = this.rootStore.getPreviousPath()
		this.rootStore.history.push(prevPagePath)
	}

	@action.bound
	setInfiniteLoaderData({
		dataLength,
		pageSize,
		totalCount
	}: {
		dataLength: number
		pageSize: number
		totalCount: number
	}) {
		const correctTo = dataLength + pageSize > totalCount ? totalCount : dataLength + pageSize
		this.infiniteLoaderData = { from: dataLength + 1, to: correctTo }
	}

	@action.bound
	showNoFiltersErrorMessage() {
		this.showErrorNotification(undefined, NO_FILTERS_ERROR_MESSAGE)
	}

	@action.bound
	setIsFilterPanelOpen(isOpen: boolean) {
		this.isFilterPanelOpen = isOpen
	}
}
